import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts'
import { apiClient } from '@services/api'
import { useAuthStore } from '@stores/authStore'

interface Attention {
  key: string
  tag: string
  title: string
  detail: string
  link: string
  severity: 'danger' | 'warn' | 'ok'
}

interface HomeData {
  date: string
  branch: string
  kpis: {
    todaysSales: { value: number; sub: string; trend: 'up' | 'down' }
    cashCreditSplit: { cash: number; credit: number }
    outstandingCredit: { value: number; stores: number }
    overdue30: { value: number; stores: number }
    payables: { value: number; bills: number }
    stockValue: { value: number; belowReorder: number }
    repackingToday: number
  }
  attention: Attention[]
  recentBills: Array<{ id: number; billNo: string; supplierName: string; itemCount: number; taxableAmount?: number; gstRate?: number; gstAmount?: number; amount: number; balance: number; paymentStatus: string; status: string }>
  todaysInvoices: Array<{ invoiceNo: string; store: string; amount: number; type: 'Cash' | 'Credit' }>
  routes: Array<{ route: string; sales: number; collected: number; outstanding: number; pct: number }>
  charts: {
    revenue30: Array<{ day: number; revenue: number; profit: number }>
    cashCredit: Array<{ week: string; cash: number; credit: number }>
    topProducts: Array<{ product: string; revenue: number }>
    aging: Array<{ bucket: string; amount: number; stores: number }>
    stockSegments: Array<{ segment: string; value: number }>
    lossTrend: Array<{ day: string; loss: number }>
  }
}

const C = {
  primary: '#1F4B44',
  accent: '#C77D1F',
  good: '#3C7A4F',
  danger: '#B3432C',
  sage: '#8FAE8E',
  sand: '#E9C889',
  grid: '#EEEDE7',
  ink: '#5B6A67',
}
const AGING_COLORS = [C.good, C.sage, C.sand, C.danger]
const STOCK_COLORS = [C.sage, C.sand, C.primary]

const formatINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const formatLakh = (n: number) => (n >= 100000 ? `₹${(n / 100000).toFixed(n >= 1000000 ? 1 : 2).replace(/\.?0+$/, '')}L` : formatINR(n))
const lakh = (v: number) => `₹${v}L`

const greeting = () => {
  const h = new Date().getHours()
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'
}

const QUICK_ACTIONS = [
  { n: 1, label: 'Purchase Entry', hint: 'Record a supplier bill', to: '/dashboard/procurement/purchase-bills' },
  { n: 2, label: 'Repacking Entry', hint: 'Log packets produced', to: '/dashboard/repacking' },
  { n: 4, label: 'Billing / Invoice', hint: 'Create a sale', to: '/dashboard/sales/invoices' },
  { n: 8, label: 'Transactions', hint: 'Record a receipt or payment', to: '/dashboard/transactions' },
  { n: 3, label: 'Stock for Sale', hint: 'Finished goods & alerts', to: '/dashboard/stock' },
  { n: 11, label: 'Reports', hint: 'GST, registers, aging', to: '/dashboard/reports' },
]

const tagClass = (severity: Attention['severity']) =>
  severity === 'danger' ? 'tag expiry' : severity === 'warn' ? 'tag stagnant' : 'tag'

const tooltipStyle = {
  contentStyle: { borderRadius: 6, border: '1px solid #DDE2DC', fontSize: 12, fontFamily: 'inherit' },
  labelStyle: { color: '#5B6A67', fontWeight: 600 },
}

function Kpi({ label, value, sub, tone, icon }: { label: string; value: string; sub: string; tone: 'primary' | 'accent' | 'danger' | 'good'; icon: string }) {
  const tones = {
    primary: { bar: C.primary, bg: '#E8EFEC', fg: C.primary },
    accent: { bar: C.accent, bg: '#FBF0DE', fg: '#8A5711' },
    danger: { bar: C.danger, bg: '#F7E6E1', fg: C.danger },
    good: { bar: C.good, bg: '#E7F2E9', fg: C.good },
  }[tone]
  return (
    <div className="kpi relative overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-[4px]" style={{ background: tones.bar }} />
      <div className="flex items-start justify-between gap-3 pl-2">
        <div>
          <div className="label">{label}</div>
          <div className="val">{value}</div>
          <div className="sub" style={{ color: tones.fg }}>
            {sub}
          </div>
        </div>
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-[15px] font-bold flex-shrink-0" style={{ background: tones.bg, color: tones.fg }}>
          {icon}
        </div>
      </div>
    </div>
  )
}

export function HomeDashboard() {
  const { user } = useAuthStore()
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-home'],
    queryFn: () => apiClient.get<any>('/dashboard/home'),
    refetchInterval: 60000,
  })
  const home: HomeData | undefined = data?.data
  const name = (user?.fullName || (user as any)?.name || 'there').split(' ')[0]
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  const k = home?.kpis
  const ch = home?.charts
  const agingTotal = ch ? ch.aging.reduce((s, a) => s + a.amount, 0) : 0
  const stockTotal = ch ? ch.stockSegments.reduce((s, a) => s + a.value, 0) : 0

  return (
    <div>
      <div className="hdr flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1>
            {greeting()}, {name}
          </h1>
          <p>
            {today}
            {home?.branch ? ` · ${home.branch}` : ''} — where the business stands right now.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/dashboard/procurement/purchase-bills" className="btn-primary btn-sm">
            + Purchase Entry
          </Link>
          <Link to="/dashboard/analytics" className="btn-ghost btn-sm">
            Analytics Dashboard →
          </Link>
        </div>
      </div>

      <div className="kpis">
        <Kpi label="Today's Sales" value={k ? formatLakh(k.todaysSales.value) : '—'} sub={k?.todaysSales.sub || ' '} tone="primary" icon="₹" />
        <Kpi label="Outstanding Credit" value={k ? formatLakh(k.outstandingCredit.value) : '—'} sub={k ? `Across ${k.outstandingCredit.stores} stores` : ' '} tone="accent" icon="⏱" />
        <Kpi label="Overdue >30 days" value={k ? formatLakh(k.overdue30.value) : '—'} sub={k ? `${k.overdue30.stores} stores need a call` : ' '} tone="danger" icon="!" />
        <Kpi
          label="Payable to Suppliers"
          value={k ? formatLakh(k.payables.value) : '—'}
          sub={k ? (k.payables.bills ? `${k.payables.bills} unpaid bills` : 'All bills settled') : ' '}
          tone={k?.payables.bills ? 'accent' : 'good'}
          icon="⇄"
        />
      </div>

      <div className="an-grid2 !mt-0">
        <div className="chartcard">
          <h2>Revenue &amp; Gross Profit — Last 30 Days</h2>
          <div className="cap">Daily sales revenue against gross profit (sale price vs traced bulk cost), ₹ lakhs</div>
          <div className="chart-wrap h260">
            {ch && (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={ch.revenue30} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C.primary} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={C.primary} stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="gProfit" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C.accent} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={C.accent} stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={C.grid} vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} interval={4} />
                  <YAxis tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} tickFormatter={lakh} />
                  <Tooltip {...tooltipStyle} formatter={(v: number, n: string) => [`₹${v}L`, n === 'revenue' ? 'Revenue' : 'Gross profit']} labelFormatter={(d) => `Day ${d}`} />
                  <Area type="monotone" dataKey="revenue" stroke={C.primary} strokeWidth={2} fill="url(#gRev)" />
                  <Area type="monotone" dataKey="profit" stroke={C.accent} strokeWidth={2} fill="url(#gProfit)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="legend-row">
            <span>
              <i style={{ background: C.primary }} />
              Revenue
            </span>
            <span>
              <i style={{ background: C.accent }} />
              Gross profit
            </span>
          </div>
        </div>

        <div className="chartcard">
          <h2>Stock Value by Segment</h2>
          <div className="cap">Today, ₹ lakhs — raw material, in repacking, finished goods</div>
          <div className="chart-wrap h260 relative">
            {ch && (
              <>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={ch.stockSegments} dataKey="value" nameKey="segment" innerRadius="62%" outerRadius="88%" paddingAngle={2} stroke="none" isAnimationActive={false}>
                      {ch.stockSegments.map((_, i) => (
                        <Cell key={i} fill={STOCK_COLORS[i % STOCK_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip {...tooltipStyle} formatter={(v: number, n: string) => [`₹${v}L (${Math.round((v / stockTotal) * 100)}%)`, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="text-[22px] font-bold tracking-[-0.02em]">₹{stockTotal.toFixed(1)}L</div>
                  <div className="text-[11px] text-ink-soft">total stock</div>
                </div>
              </>
            )}
          </div>
          <div className="legend-row">
            {ch?.stockSegments.map((s, i) => (
              <span key={s.segment}>
                <i style={{ background: STOCK_COLORS[i] }} />
                {s.segment} ₹{s.value}L
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="an-grid3">
        <div className="chartcard">
          <h2>Cash vs Credit Sales</h2>
          <div className="cap">This month, ₹ lakhs per week</div>
          <div className="chart-wrap h220">
            {ch && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ch.cashCredit} margin={{ top: 8, right: 4, left: -18, bottom: 0 }} barCategoryGap="30%">
                  <CartesianGrid stroke={C.grid} vertical={false} />
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} tickFormatter={lakh} />
                  <Tooltip {...tooltipStyle} formatter={(v: number, n: string) => [`₹${v}L`, n === 'cash' ? 'Cash' : 'Credit']} />
                  <Bar dataKey="cash" stackId="a" fill={C.good} radius={[0, 0, 4, 4]} />
                  <Bar dataKey="credit" stackId="a" fill={C.accent} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="legend-row">
            <span>
              <i style={{ background: C.good }} />
              Cash
            </span>
            <span>
              <i style={{ background: C.accent }} />
              Credit
            </span>
          </div>
        </div>

        <div className="chartcard">
          <h2>Top Products by Revenue</h2>
          <div className="cap">This month, ₹ lakhs</div>
          <div className="chart-wrap h220">
            {ch && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ch.topProducts} layout="vertical" margin={{ top: 4, right: 28, left: 8, bottom: 0 }} barCategoryGap="28%">
                  <CartesianGrid stroke={C.grid} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} tickFormatter={lakh} />
                  <YAxis type="category" dataKey="product" tick={{ fontSize: 11.5, fill: C.ink }} tickLine={false} axisLine={false} width={66} />
                  <Tooltip {...tooltipStyle} formatter={(v: number) => [`₹${v}L`, 'Revenue']} />
                  <Bar dataKey="revenue" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11, fill: C.ink, formatter: (v: number) => `₹${v}L` }}>
                    {ch.topProducts.map((_, i) => (
                      <Cell key={i} fill={i === 0 ? C.accent : C.primary} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="chartcard">
          <h2>Credit Aging</h2>
          <div className="cap">Outstanding ₹{agingTotal.toFixed(1)}L across {ch ? ch.aging.reduce((s, a) => s + a.stores, 0) : '—'} stores</div>
          <div className="chart-wrap h220 relative">
            {ch && (
              <>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={ch.aging} dataKey="amount" nameKey="bucket" innerRadius="60%" outerRadius="88%" paddingAngle={2} stroke="none" isAnimationActive={false}>
                      {ch.aging.map((_, i) => (
                        <Cell key={i} fill={AGING_COLORS[i]} />
                      ))}
                    </Pie>
                    <Tooltip {...tooltipStyle} formatter={(v: number, n: string, p: any) => [`₹${v}L · ${p.payload.stores} stores`, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="text-[18px] font-bold text-danger">₹{ch.aging[3].amount}L</div>
                  <div className="text-[10.5px] text-ink-soft">30+ days</div>
                </div>
              </>
            )}
          </div>
          <div className="legend-row">
            {ch?.aging.map((a, i) => (
              <span key={a.bucket}>
                <i style={{ background: AGING_COLORS[i] }} />
                {a.bucket}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="an-grid2">
        <div className="card">
          <h2>
            Needs Attention <small>— ranked by risk</small>
          </h2>
          {isLoading && <div className="text-center py-6 text-ink-soft text-[13px]">Loading…</div>}
          {home?.attention.map((a) => (
            <Link key={a.key} to={a.link} className="alert-item hover:bg-[#FAFAF7] no-underline text-ink">
              <div className="left">
                <b>{a.title}</b>
                <div className="qmeta">{a.detail}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className={tagClass(a.severity)}>{a.tag}</div>
                <div className="qmeta text-[11px] text-primary-500 mt-1">Open →</div>
              </div>
            </Link>
          ))}
          <div className="hint">Alerts refresh every minute. Click any item to open the screen where it's handled.</div>
        </div>

        <div>
          <div className="card">
            <h2>
              Quick Actions <small>— jump straight into today's work</small>
            </h2>
            <div className="grid grid-cols-2 gap-[10px]">
              {QUICK_ACTIONS.map((a) => (
                <Link
                  key={a.n}
                  to={a.to}
                  className="flex items-center gap-3 border border-line rounded-[6px] px-[12px] py-[11px] hover:border-primary-500 hover:bg-primary-soft transition-colors"
                >
                  <span className="w-[22px] h-[22px] rounded-full bg-primary-500 text-white text-[10.5px] font-semibold flex items-center justify-center flex-shrink-0">
                    {a.n}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13px] font-semibold text-ink truncate">{a.label}</span>
                    <span className="block text-[11.5px] text-ink-soft truncate">{a.hint}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div className="chartcard mt-[18px]">
            <h2>Repacking Loss Trend</h2>
            <div className="cap">Validated loss value, ₹ per day — {k?.repackingToday ?? '—'} sessions today, {k?.stockValue.belowReorder ?? '—'} SKUs below reorder</div>
            <div className="chart-wrap" style={{ height: 150 }}>
              {ch && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={ch.lossTrend} margin={{ top: 8, right: 8, left: -14, bottom: 0 }}>
                    <CartesianGrid stroke={C.grid} vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${v}`} />
                    <Tooltip {...tooltipStyle} formatter={(v: number) => [formatINR(v), 'Loss']} />
                    <Line type="monotone" dataKey="loss" stroke={C.danger} strokeWidth={2} dot={{ r: 3, fill: C.danger, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="an-grid2">
        <div className="card">
          <div className="field-label-row">
            <h2 className="!m-0">Recent Purchase Bills</h2>
            <Link to="/dashboard/procurement/purchase-bills/list" className="btn-ghost btn-xs">
              View all bills →
            </Link>
          </div>
          <table className="mt-[10px]">
            <thead>
              <tr>
                <th>Bill</th>
                <th>Supplier</th>
                <th className="num">Items</th>
                <th className="num">GST</th>
                <th className="num">Grand Total</th>
                <th>Payment</th>
              </tr>
            </thead>
            <tbody>
              {(home?.recentBills || []).map((b) => (
                <tr key={b.id}>
                  <td>
                    <Link to={`/dashboard/procurement/purchase-bills/${b.id}`} className="text-primary-500 font-semibold hover:underline">
                      {b.billNo}
                    </Link>
                  </td>
                  <td>{b.supplierName}</td>
                  <td className="num">{b.itemCount}</td>
                  <td className="num">
                    {formatINR(b.gstAmount ?? 0)}
                    <div className="text-[10.5px] text-ink-soft">{b.gstRate ?? 0}%</div>
                  </td>
                  <td className="num font-semibold">{formatINR(b.amount)}</td>
                  <td>
                    <span
                      className={`badge ${
                        b.paymentStatus === 'Paid' ? 'badge-full' : b.paymentStatus === 'Partial' ? 'badge-partial' : b.paymentStatus === 'Draft' ? 'badge-draft' : 'badge-unpaid'
                      }`}
                    >
                      {b.paymentStatus}
                    </span>
                  </td>
                </tr>
              ))}
              {home && home.recentBills.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-ink-soft">
                    No purchase bills yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="card">
          <div className="field-label-row">
            <h2 className="!m-0">Today's Invoices</h2>
            <Link to="/dashboard/sales/invoices" className="btn-ghost btn-xs">
              Billing →
            </Link>
          </div>
          <table className="mt-[10px]">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Store</th>
                <th className="num">Amount</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {(home?.todaysInvoices || []).map((inv) => (
                <tr key={inv.invoiceNo}>
                  <td>{(inv as any).id ? <Link to={`/dashboard/sales/invoices/${(inv as any).id}`} className="font-semibold text-primary-500 hover:underline">{inv.invoiceNo}</Link> : inv.invoiceNo}</td>
                  <td>{inv.store}</td>
                  <td className="num">{formatINR(inv.amount)}</td>
                  <td>
                    <span className={`badge ${inv.type === 'Credit' ? 'badge-credit' : 'badge-cash'}`}>{inv.type}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {k && (
            <div className="mt-[14px]">
              <div className="flex justify-between text-[11.5px] text-ink-soft mb-1">
                <span>Cash {k.cashCreditSplit.cash}%</span>
                <span>Credit {k.cashCreditSplit.credit}%</span>
              </div>
              <div className="h-[8px] rounded-full overflow-hidden flex bg-line">
                <div style={{ width: `${k.cashCreditSplit.cash}%`, background: C.good }} />
                <div style={{ width: `${k.cashCreditSplit.credit}%`, background: C.accent }} />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card mt-[18px]">
        <h2>
          Branch / Route Performance <small>— this month: sales, collections, and outstanding credit</small>
        </h2>
        <table>
          <thead>
            <tr>
              <th>Route</th>
              <th className="num">Sales</th>
              <th className="num">Collected</th>
              <th className="num">Outstanding</th>
              <th className="w-[220px]">Collection %</th>
            </tr>
          </thead>
          <tbody>
            {(home?.routes || []).map((r) => (
              <tr key={r.route}>
                <td>{r.route}</td>
                <td className="num">{formatLakh(r.sales)}</td>
                <td className="num">{formatLakh(r.collected)}</td>
                <td className="num">{r.outstanding ? formatLakh(r.outstanding) : '₹0'}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-[8px] rounded-full bg-line overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${r.pct}%`, background: r.pct >= 85 ? C.good : r.pct >= 75 ? C.accent : C.danger }} />
                    </div>
                    <span className={`badge ${r.pct >= 85 ? 'badge-ok' : 'badge-loss'}`}>{r.pct}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
